import React, { useState, useRef, useEffect } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const DEFAULT_LAYOUT = [
    { i: 'camera', x: 0, y: 0, w: 4, h: 10 },
    { i: 'chat', x: 4, y: 0, w: 8, h: 10 }
];

interface ChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
}

export const LiveVisionTerminal: React.FC = () => {
    const [isActive, setIsActive] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [chatInput, setChatInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);

    const [fps, setFps] = useState(1);
    const [resolution, setResolution] = useState(360);
    const [systemPrompt, setSystemPrompt] = useState('Jesteś wbudowanym asystentem sztucznej inteligencji. Na bieżąco widzisz co się dzieje na kamerze. Odpowiadaj krótko i po polsku na polecenia.');

    // NEW: Camera Source switching
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const captureIntervalRef = useRef<number | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    // Draggable Layout State
    const [isLocked, setIsLocked] = useState(true);
    const [layouts, setLayouts] = useState<any>({ lg: DEFAULT_LAYOUT });
    const [mounted, setMounted] = useState(false);
    
    // Custom width provider
    const [width, setWidth] = useState(1200);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setMounted(true);
        const saved = localStorage.getItem('omnigate_vision_layout');
        if (saved) {
            try { const p = JSON.parse(saved);
                if (p) { Object.keys(p).forEach(k => { if (Array.isArray(p[k])) p[k].forEach((i:any) => delete i.static); }); }
                setLayouts(p); } catch { }
        }

        if (containerRef.current) {
            setWidth(containerRef.current.clientWidth);
        }

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.contentRect.width > 0) {
                    setWidth(entry.contentRect.width);
                }
            }
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    const onLayoutChange = (layout: any, allLayouts: any) => {
        setLayouts(allLayouts);
        localStorage.setItem('omnigate_vision_layout', JSON.stringify(allLayouts));
    };

    useEffect(() => {
        navigator.mediaDevices.enumerateDevices().then(deviceInfos => {
            const videoDevices = deviceInfos.filter(d => d.kind === 'videoinput');
            setDevices(videoDevices);
            if (videoDevices.length > 0) {
                setSelectedDeviceId(videoDevices[0].deviceId);
            }
        });
    }, []);

    const stopStream = () => {
        setIsActive(false);
        if (captureIntervalRef.current) window.clearInterval(captureIntervalRef.current);
        if (wsRef.current) wsRef.current.close();
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setMessages(prev => [...prev, { role: 'system', content: 'Rozłączono ze strumieniem Live Vision.' }]);
    };

    const startStream = async () => {
        try {
            const constraints: MediaStreamConstraints = {
                video: {
                    width: 1280,
                    height: 720,
                    deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined
                }
            };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }

            const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const ws = new WebSocket(`${wsProtocol}//${window.location.host}/ws/live`);
            wsRef.current = ws;

            ws.onopen = () => {
                setIsActive(true);
                setMessages([{ role: 'system', content: 'Połączono z silnikiem MiniCPM Vision!' }]);
                ws.send(JSON.stringify({ type: 'update_prompt', prompt: systemPrompt }));
                startCaptureLoop(ws);
            };

            ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.type === 'response.created') {
                    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
                } else if (data.type === 'response.text.delta') {
                    setMessages(prev => {
                        const newMsgs = [...prev];
                        newMsgs[newMsgs.length - 1].content += data.delta;
                        return newMsgs;
                    });
                } else if (data.type === 'response.done') {
                    setIsTyping(false);
                } else if (data.type === 'prompt.updated') {
                    setMessages(prev => [...prev, { role: 'system', content: '✓ Kontekst wideo i ramy agenta zaktualizowane.' }]);
                }
            };

            ws.onerror = (error) => {
                console.error("Live Vision WS Error:", error);
                setMessages(prev => [...prev, { role: 'system', content: 'Błąd połączenia z gniazdem WebSocet.' }]);
                stopStream();
            };

        } catch (err: any) {
            alert("Błąd dostępu do kamery: " + err.message);
        }
    };

    const startCaptureLoop = (ws: WebSocket) => {
        if (captureIntervalRef.current) window.clearInterval(captureIntervalRef.current);

        captureIntervalRef.current = window.setInterval(() => {
            if (!videoRef.current || !canvasRef.current || ws.readyState !== WebSocket.OPEN) return;
            const ctx = canvasRef.current.getContext('2d');
            if (!ctx) return;

            const ratio = videoRef.current.videoWidth / videoRef.current.videoHeight;
            canvasRef.current.width = resolution * ratio;
            canvasRef.current.height = resolution;

            ctx.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
            const base64 = canvasRef.current.toDataURL('image/jpeg', 0.6).split(',')[1];

            ws.send(JSON.stringify({ type: 'video_frame', image: base64 }));
        }, 1000 / fps);
    };

    useEffect(() => {
        if (isActive && wsRef.current) {
            startCaptureLoop(wsRef.current);
        }
    }, [fps, resolution]);

    const updatePrompt = () => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'update_prompt', prompt: systemPrompt }));
        }
    };

    const sendMessage = () => {
        if (!wsRef.current) return;
        const textToSend = chatInput.trim() || "Przeanalizuj obecny obraz i opisz go w skrócie.";
        setMessages(prev => [...prev, { role: 'user', content: textToSend }]);
        setIsTyping(true);
        wsRef.current.send(JSON.stringify({ type: 'user_message', text: textToSend }));
        setChatInput('');
    };

    if (!mounted) return null;

    return (
        <div ref={containerRef} className="flex flex-col h-full bg-[#131722] overflow-hidden min-h-[600px] relative pb-10">
            <style>{`
                .react-grid-layout { position: relative; transition: height 200ms ease; }
                .react-grid-item { transition: all 200ms ease; transition-property: left, top; }
                .react-grid-item.cssTransforms { transition-property: transform; }
                .react-grid-item.resizing { z-index: 1; will-change: width, height; }
                .react-grid-item.react-draggable-dragging { transition: none; z-index: 3; will-change: transform; }
                .react-grid-item.react-grid-placeholder { background: rgba(168, 85, 247, 0.2); border: 2px dashed rgba(168, 85, 247, 0.5); opacity: 0.5; z-index: 2; transition: none; }
                .drag-handle { cursor: grab; }
                .drag-handle:active { cursor: grabbing; }
                .panel-wrapper { height: 100%; display: flex; flex-direction: column; background: #1e222d; border: 1px solid #2a2e39; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); }
                .panel-wrapper:hover { border-color: rgba(168, 85, 247, 0.3); }
            `}</style>

            <div className="absolute -top-10 right-4 z-50 flex gap-2">
                <button onClick={() => setIsLocked(!isLocked)} className={`text-[9px] px-2 py-1 rounded border transition-colors ${isLocked ? 'bg-red-900/40 text-red-400 border-red-800' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
                    <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Locked' : 'Unlocked'}
                </button>
                <button onClick={() => { setLayouts({ lg: DEFAULT_LAYOUT }); localStorage.removeItem('omnigate_vision_layout'); }} className="text-[9px] bg-slate-800 text-slate-400 hover:text-white px-2 py-1 rounded border border-slate-700">
                    <i className="fas fa-sync-alt mr-1"></i> Reset Layout
                </button>
            </div>

            
                <div className="flex justify-between items-center sm:mb-4 mb-2 px-2 bg-[#131722]/80 border border-[#2a2e39] rounded py-2">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setIsLocked(!isLocked)} className={`text-[10px] px-3 py-1.5 rounded transition-all font-bold ${isLocked ? 'bg-red-500/20 text-red-400 border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 'bg-green-500/20 text-green-400 border border-green-500/50'}`}>
                            <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Blokada Włączona' : 'Odblokowane (Przeciągaj)'}
                        </button>
                    </div>
                </div>
    <ResponsiveGridLayout
                className="layout flex-1"
                width={width}
                layouts={isLocked ? Object.fromEntries(Object.entries(layouts).map(([k, v]) => [k, v.map((i: any) => ({ ...i, static: true }))])) : layouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={50}
                onLayoutChange={onLayoutChange}
                // @ts-ignore
                isDraggable={!isLocked}
                // @ts-ignore
                isResizable={!isLocked}
                // @ts-ignore
                draggableHandle=".drag-handle"
                margin={[12, 12]}
            >
                {/* CAMERA & SETTINGS PANEL */}
                <div key="camera" className="panel-wrapper">
                    <div className={`p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-video text-purple-500"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Live Camera</h2>
                        </div>
                        <i className="fas fa-grip-lines text-slate-600"></i>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 bg-[#0a0c10] flex flex-col space-y-4 custom-scrollbar">
                        <div className="flex flex-col gap-2 relative">
                            {devices.length > 1 && !isActive && (
                                <select
                                    value={selectedDeviceId}
                                    onChange={e => setSelectedDeviceId(e.target.value)}
                                    className="w-full text-[10px] bg-[#2a2e39] text-gray-300 border border-gray-600 rounded px-2 py-1.5 focus:outline-none focus:border-purple-500/50"
                                >
                                    {devices.map(d => (
                                        <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.substring(0, 5)}`}</option>
                                    ))}
                                </select>
                            )}
                            {isActive ? (
                                <button onClick={stopStream} className="w-full text-[10px] bg-red-600/20 text-red-500 px-3 py-2 rounded border border-red-500/30 uppercase font-black tracking-wider hover:bg-red-600/40 transition-colors">Rozłącz Serwer (Stop)</button>
                            ) : (
                                <button onClick={startStream} className="w-full text-[10px] bg-green-500/20 text-green-400 px-3 py-2 rounded border border-green-500/30 uppercase font-black tracking-wider hover:bg-green-500/40 transition-colors">Rozpocznij Transmisję</button>
                            )}
                        </div>

                        <div className="relative rounded overflow-hidden aspect-video bg-black border border-[#2a2e39] flex-shrink-0">
                            <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${isActive ? '' : 'hidden'}`} />
                            {!isActive && <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-500 uppercase">Kamera Wyłączona / Ready</div>}
                            <canvas ref={canvasRef} className="hidden" />
                        </div>

                        <div className="space-y-4">
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase">Klatkaż (FPS)</label>
                                    <span className="text-[10px] font-black text-purple-400">{fps} kl/s</span>
                                </div>
                                <input type="range" min="0.2" max="5.0" step="0.1" value={fps} onChange={e => setFps(parseFloat(e.target.value))} className="w-full accent-purple-500 h-1 bg-[#2a2e39] rounded-lg appearance-none cursor-pointer" />
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase">Skala Obrazu (px)</label>
                                    <span className="text-[10px] font-black text-blue-400">{resolution}p</span>
                                </div>
                                <input type="range" min="144" max="720" step="144" value={resolution} onChange={e => setResolution(parseInt(e.target.value))} className="w-full accent-blue-500 h-1 bg-[#2a2e39] rounded-lg appearance-none cursor-pointer" />
                            </div>

                            <div className="pt-2 border-t border-[#2a2e39]">
                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">System Prompt / Persona Wideo</label>
                                <textarea
                                    value={systemPrompt}
                                    onChange={e => setSystemPrompt(e.target.value)}
                                    className="w-full h-24 bg-[#131722] border border-[#2a2e39] rounded p-2 text-[10px] text-slate-300 focus:outline-none focus:border-purple-500/50 resize-none"
                                    placeholder="Jak model ma analizować ten stream..."
                                />
                                <button
                                    onClick={updatePrompt}
                                    disabled={!isActive}
                                    className="mt-2 w-full text-[9px] bg-slate-700 text-slate-300 py-1.5 rounded uppercase font-bold disabled:opacity-50 hover:bg-slate-600 transition-colors"
                                >
                                    Aktualizuj Prompt
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* VISION CHAT PANEL */}
                <div key="chat" className="panel-wrapper">
                    <div className={`p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-comment-dots text-purple-500"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Vision Interaction</h2>
                        </div>
                        <i className="fas fa-grip-lines text-slate-600"></i>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#0a0c10] custom-scrollbar">
                        {messages.length === 0 && (
                            <div className="h-full flex flex-col items-center justify-center text-slate-600">
                                <i className="fas fa-video-slash text-3xl mb-3 opacity-50"></i>
                                <p className="text-[10px] uppercase font-bold tracking-widest text-center max-w-[200px]">Uruchom transmisję by rozpocząć analizę strumienia The Omni Engine.</p>
                            </div>
                        )}
                        {messages.map((msg, i) => (
                            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[80%] rounded-xl p-3 text-[11px] ${msg.role === 'user' ? 'bg-purple-600 text-white shadow-lg' :
                                    msg.role === 'system' ? 'bg-[#131722] border border-[#2a2e39] text-slate-400 flex items-center gap-2' :
                                        'bg-[#1e222d] border border-[#2a2e39] text-slate-200'
                                    }`}>
                                    {msg.content === '' && msg.role === 'assistant' ? (
                                        <div className="flex gap-1 items-center px-2 py-1">
                                            <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce"></div>
                                            <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                            <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                        </div>
                                    ) : (
                                        <p className="font-mono leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                        {isTyping && (
                            <div className="flex justify-start">
                                <div className="max-w-[80%] rounded-xl p-3 text-[11px] bg-[#1e222d] border border-[#2a2e39] text-slate-200">
                                    <div className="flex gap-1 items-center px-2 py-1">
                                        <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce"></div>
                                        <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                        <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="p-4 bg-[#1e222d] border-t border-[#2a2e39]">
                        <div className="relative flex items-center gap-2">
                            <input
                                disabled={!isActive}
                                type="text"
                                value={chatInput}
                                onChange={e => setChatInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && sendMessage()}
                                className="flex-1 bg-[#0a0c10] border border-[#2a2e39] text-slate-200 text-[11px] font-mono px-4 py-3 rounded-lg focus:outline-none focus:border-purple-500/50 disabled:opacity-50"
                                placeholder={isActive ? "Zanotuj lub zapytaj odnośnie obrazu (Enter)..." : "Transmisja wideo jest zastopowana..."}
                            />
                            <button
                                disabled={!isActive}
                                onClick={sendMessage}
                                className="w-11 h-11 shrink-0 flex items-center justify-center bg-purple-600 rounded-lg text-white disabled:opacity-50 hover:bg-purple-500 transition-colors shadow-lg"
                            >
                                <i className="fas fa-paper-plane text-xs"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </ResponsiveGridLayout>
        </div>
    );
};
